import { AgentCompanySize, AgentType } from '@/constants';
import React from 'react';
import FormItem from '../FormItem';
import { Control, Controller, FieldErrors, UseFormRegister } from 'react-hook-form';
import { FormValidation } from '@/helpers/formValidations';
import Select from 'react-select';
import { ErrorMessage } from '@hookform/error-message';
import { useGetAllCountries } from '@/hooks/dataProvider';
import { AgentDetailModel } from '@/__generated';
import { useTranslation } from 'react-i18next';

type Props = {
  register: UseFormRegister<any>;
  errors: FieldErrors<any>;
  control: Control<any, any>;
  profile?: AgentDetailModel | undefined | null;
};

export default function CompanyDetailForm({ register, errors, control, profile }: Props) {
  const { t } = useTranslation();
  const { data, isLoading } = useGetAllCountries();
  const countries = React.useMemo(() => {
    return (
      data?.data?.map(d => {
        return { value: d.shortCode, label: `[${d.phoneCode}] ${d.name}` };
      }) || []
    );
  }, [data]);
  return (
    <>
      {/* End .col */}
      <div className="col-12 d-flex align-items-center">
        <div style={{ flex: 1, border: '1px dashed #cccccc' }} />
        <div className="fw-bold text-18 text-blue-1  rounded-2 px-10 text-uppercase">{t('agentRegister_CompanyDetail')}</div>
        <div style={{ flex: 1, border: '1px dashed #cccccc' }} />
      </div>

      <div className="col-6">
        <FormItem isRequired errors={errors} label={t('agentRegister_CompanyName')} name="companyName">
          <input type="text" {...register('companyName', FormValidation.Required)} />
        </FormItem>
      </div>
      <div className="col-6">
        <FormItem errors={errors} label={t('agentRegister_IATAId')} name="iataId">
          <input type="text" {...register('iataId')} />
        </FormItem>
      </div>

      <div className="col-6">
        {profile ? (
          <FormItem errors={errors} label={t('agentRegister_CompanyType')} name="agentType">
            <input value={AgentType.__getValue(profile.agentType)} disabled={!!profile} />
          </FormItem>
        ) : (
          <div className="form-item-select">
            <div className={`lh-1 text-14 text-light-1 label required`}>{t('agentRegister_CompanyType')}</div>
            <div className="select-container">
              <Controller
                control={control}
                rules={FormValidation.Required}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Select
                    isDisabled={!!profile}
                    classNamePrefix="omega-select"
                    className="w-100"
                    placeholder=""
                    options={AgentType.__getAgentSelectOptions()}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                )}
                name="agentType"
              />
            </div>
          </div>
        )}

        <ErrorMessage errors={errors} name={'agentType'} as="span" className="form-item-error" />
      </div>

      <div className="col-6">
        <div className="form-item-select">
          <div className={`lh-1 text-14 text-light-1 label`}>Company size</div>
          <div className="select-container">
            <Controller
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Select
                  classNamePrefix="omega-select"
                  className="w-100"
                  placeholder=""
                  options={AgentCompanySize.__getSelectOptions()}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  isClearable
                />
              )}
              name="companySize"
            />
          </div>
        </div>
        <ErrorMessage errors={errors} name={'companySize'} as="span" className="form-item-error" />
      </div>

      <div className="col-6">
        <FormItem isRequired errors={errors} label={t('base.Address')} name="companyAddress">
          <input type="text" {...register('companyAddress', FormValidation.Required)} />
        </FormItem>
      </div>
      <div className="col-6">
        <FormItem isRequired errors={errors} label={t('base.CityName')} name="cityName">
          <input type="text" {...register('cityName', FormValidation.Required)} />
        </FormItem>
      </div>

      <div className="col-3">
        <div className="form-item-select">
          <div className={`lh-1 text-14 text-light-1 label required`}>{t('base.Country')}</div>
          <div className="select-container">
            <Controller
              control={control}
              rules={FormValidation.Required}
              render={({ field: { onChange, onBlur, value } }) => (
                <Select
                  isLoading={isLoading}
                  classNamePrefix="omega-select"
                  className="w-100"
                  placeholder=""
                  options={countries}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
              name="country"
            />
          </div>
        </div>
        <ErrorMessage errors={errors} name={'country'} as="span" className="form-item-error" />
      </div>

      <div className="col-3">
        <FormItem isRequired errors={errors} label={t('base.PhoneNumber')} name="companyPhoneNumber">
          <input type="text" {...register('companyPhoneNumber', { ...FormValidation.Required, ...FormValidation.PhoneNumber })} />
        </FormItem>
      </div>
      <div className="col-6">
        <FormItem isRequired errors={errors} label={t('base.PostalCode')} name="companyPostalCode">
          <input type="text" {...register('companyPostalCode', FormValidation.Required)} />
        </FormItem>
      </div>
      <div className="col-6">
        <FormItem isRequired errors={errors} label={t('base.Email')} name="companyEmailAddress">
          <input type="text" {...register('companyEmailAddress', { ...FormValidation.Required, ...FormValidation.Email })} />
        </FormItem>
      </div>

      <div className="col-6">
        <FormItem isRequired errors={errors} label="Company website" name="companyWebsite">
          <input type="text" {...register('companyWebsite', { ...FormValidation.Required })} />
        </FormItem>
      </div>
    </>
  );
}
